import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { tenantAPI, unitAPI, invitationAPI, documentAPI, exportAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Users,
  Mail,
  Phone,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  FolderOpen,
  Download,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const Tenants = () => {
  const { t } = useI18n();
  const [tenants, setTenants] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', phone: '', unit_id: '' });
  const [docsModal, setDocsModal] = useState(null);
  const [tenantDocs, setTenantDocs] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await exportAPI.tenants();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tenants_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Tenants exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const loadData = async () => {
    try {
      const [tenantsRes, unitsRes, invitesRes] = await Promise.all([
        tenantAPI.getAll(),
        unitAPI.getAll(),
        invitationAPI.list(),
      ]);
      setTenants(tenantsRes.data);
      setUnits(unitsRes.data.filter((u) => u.status === 'VACANT'));
      setInvitations(invitesRes.data);
    } catch (error) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await invitationAPI.create({
        email: inviteForm.email,
        name: inviteForm.name,
        phone: inviteForm.phone || null,
        unit_id: inviteForm.unit_id || null,
      });
      toast.success('Invitation sent successfully');
      setDialogOpen(false);
      setInviteForm({ email: '', name: '', phone: '', unit_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleRevoke = async (id) => {
    try {
      await invitationAPI.revoke(id);
      toast.success('Invitation revoked');
      loadData();
    } catch (error) {
      toast.error('Failed to revoke invitation');
    }
  };

  const pendingInvites = invitations.filter((i) => i.status === 'pending');
  const pastInvites = invitations.filter((i) => i.status !== 'pending');

  const statusIcon = (status) => {
    if (status === 'accepted') return <CheckCircle className="w-4 h-4 text-emerald-400" />;
    if (status === 'expired' || status === 'revoked') return <XCircle className="w-4 h-4 text-red-400" />;
    return <Clock className="w-4 h-4 text-amber-400" />;
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('tenants.title')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title={t('tenants.title')}>
      <div className="space-y-8" data-testid="tenants-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">{t('tenants.subtitle')}</p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-border text-muted-foreground hover:text-foreground"
              onClick={handleExportCSV}
              disabled={exporting || tenants.length === 0}
              data-testid="export-tenants-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gold text-white hover:bg-gold-light" data-testid="invite-tenant-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Tenant
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[420px] bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif">Invite New Tenant</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Tenant Name</Label>
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="Full name"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Email Address</Label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="tenant@example.com"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Phone (Optional)</Label>
                  <Input
                    type="tel"
                    value={inviteForm.phone}
                    onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="+1 (555) 000-0000"
                    data-testid="invite-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Assign to Unit (Optional)</Label>
                  <select
                    value={inviteForm.unit_id}
                    onChange={(e) => setInviteForm({ ...inviteForm, unit_id: e.target.value })}
                    className="w-full h-10 px-3 bg-background border border-[#e5e7eb] text-foreground rounded-lg"
                    data-testid="invite-unit-select"
                  >
                    <option value="">No unit assigned yet</option>
                    {units.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.unit_number} - ${unit.rent_amount}/mo
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gold text-white hover:bg-gold-light"
                  data-testid="send-invite-btn"
                >
                  Send Invitation
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Pending Invitations */}
        {pendingInvites.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Pending Invitations ({pendingInvites.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="px-6 py-4 flex items-center justify-between" data-testid={`pending-invite-${invite.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-500/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium">{invite.name}</p>
                        <p className="text-sm text-muted-foreground">{invite.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 text-xs uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        Pending
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => handleRevoke(invite.id)}
                        data-testid={`revoke-invite-${invite.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Tenants */}
        {tenants.length === 0 && pendingInvites.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">{t('tenants.noTenants')}</h3>
              <p className="text-muted-foreground mb-6">{t('tenants.noTenantsDesc')}</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-gold text-white hover:bg-gold-light">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Your First Tenant
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tenants.map((tenant, index) => (
              <Card
                key={tenant.id}
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`tenant-card-${tenant.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-medium">{tenant.name}</h3>
                      <p className="text-sm text-muted-foreground">Tenant</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">{tenant.email}</span>
                    </div>
                    {tenant.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">{tenant.phone}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <span className="px-3 py-1 text-xs uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      Active
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-gold"
                      onClick={async () => {
                        try {
                          const res = await documentAPI.tenantDocuments(tenant.id);
                          setTenantDocs(res.data);
                          setDocsModal(tenant);
                        } catch { toast.error('Failed to load documents'); }
                      }}
                      data-testid={`view-docs-${tenant.id}`}
                    >
                      <FolderOpen className="w-4 h-4 mr-1" />
                      Docs
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Past Invitations */}
        {pastInvites.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="font-serif text-lg">Invitation History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {pastInvites.map((invite) => (
                  <div key={invite.id} className="px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {statusIcon(invite.status)}
                      <div>
                        <p className="text-sm">{invite.name} ({invite.email})</p>
                      </div>
                    </div>
                    <span className={`text-xs uppercase tracking-wider ${
                      invite.status === 'accepted' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {invite.status}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tenant Documents Modal */}
      <Dialog open={!!docsModal} onOpenChange={() => { setDocsModal(null); setTenantDocs(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-gold" />
              Documents: {docsModal?.name}
            </DialogTitle>
          </DialogHeader>
          {tenantDocs && (
            <div className="space-y-4" data-testid="tenant-docs-modal">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Lease Documents ({tenantDocs.lease_documents.length})</p>
                {tenantDocs.lease_documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No lease documents uploaded</p>
                ) : (
                  <div className="space-y-2">
                    {tenantDocs.lease_documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-white border border-border text-sm">
                        <span>{doc.filename}</span>
                        <span className="text-xs text-muted-foreground">{doc.property_name} · Unit {doc.unit_number}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Payment Receipts ({tenantDocs.payment_receipts.length})</p>
                {tenantDocs.payment_receipts.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No payment receipts yet</p>
                ) : (
                  <div className="space-y-2">
                    {tenantDocs.payment_receipts.map(r => (
                      <div key={r.payment_id} className="flex items-center justify-between p-2 bg-white border border-border text-sm">
                        <div>
                          <span>{r.description}</span>
                          <span className="text-muted-foreground ml-2">Unit {r.unit_number}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-emerald-400">${r.amount.toFixed(2)}</span>
                          <span className="text-xs text-muted-foreground ml-2">{r.paid_date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Tenants;
